/**
 * authService.js — MSAL authentication service for dual M365 accounts
 *
 * Manages two independent Microsoft 365 authentications using a single
 * MSAL PublicClientApplication instance. Each account (Invero + Work 2)
 * authenticates via an interactive popup and gets its own access token.
 *
 * Key design decisions:
 *   - Single MSAL instance with /common authority (multi-tenant)
 *   - Interactive popup login (not redirect) — avoids losing app state
 *   - Tokens held in browser memory only (MSAL in-memory cache)
 *   - Silent token renewal attempted first; falls back to popup if expired
 *   - Each account identified by its homeAccountId from MSAL
 *
 * Why popup instead of redirect?
 *   With two accounts to authenticate, redirect flow would lose context
 *   of the first login during the second redirect. Popup keeps the main
 *   window intact and lets us chain two logins cleanly.
 */

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, graphScopes } from '../config/msalConfig';

// Singleton MSAL instance — initialized once, shared across the app.
// Both Invero and Work 2 logins go through this same instance.
let msalInstance = null;

/**
 * Initialize the MSAL instance.
 * Must be called once before any auth operations.
 * Returns a promise that resolves when MSAL is ready.
 */
export async function initializeMsal() {
  if (msalInstance) return msalInstance;

  msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  // Handle redirect promise in case we ever switch to redirect flow
  await msalInstance.handleRedirectPromise();

  return msalInstance;
}

/**
 * Get the current MSAL instance.
 * Throws if initializeMsal() hasn't been called.
 */
export function getMsalInstance() {
  if (!msalInstance) {
    throw new Error('MSAL not initialized. Call initializeMsal() first.');
  }
  return msalInstance;
}

/**
 * Trigger an interactive login popup for a specific calendar account.
 *
 * The loginHint parameter is optional — if provided, it pre-fills
 * the email field in the Microsoft login popup, making it clear
 * which account the user should sign into.
 *
 * @param {string} [loginHint] - Email hint (e.g., 'user@invero.com')
 * @returns {object} Account info from MSAL (includes homeAccountId, username, name)
 */
export async function loginWithPopup(loginHint) {
  const instance = getMsalInstance();

  const loginRequest = {
    scopes: graphScopes.calendar,
    prompt: 'select_account', // Always show account picker — important for dual-account
  };

  // If a hint is provided, add it so the user sees the right email pre-filled.
  // We still use select_account prompt so they can switch if needed.
  if (loginHint) {
    loginRequest.loginHint = loginHint;
  }

  try {
    const response = await instance.loginPopup(loginRequest);
    return response.account;
  } catch (error) {
    // User cancelled the popup or a real error occurred
    if (error.errorCode === 'user_cancelled') {
      throw new Error('Login cancelled by user.');
    }
    throw error;
  }
}

/**
 * Acquire an access token for a specific account.
 *
 * Tries silent acquisition first (uses cached/refresh token).
 * If that fails (token expired, refresh token gone), falls back
 * to an interactive popup.
 *
 * This is the function called before every Graph API request.
 *
 * @param {object} account - MSAL account object (from loginWithPopup result)
 * @returns {string} Access token for Microsoft Graph API
 * @throws {Error} If token acquisition fails even after interactive fallback
 */
export async function acquireToken(account) {
  const instance = getMsalInstance();

  const silentRequest = {
    scopes: graphScopes.calendar,
    account: account,
  };

  try {
    // Try silent first — this works if the access token is still valid
    // or if the refresh token can get a new access token without user interaction.
    const response = await instance.acquireTokenSilent(silentRequest);
    return response.accessToken;
  } catch (error) {
    // InteractionRequiredAuthError means the silent approach failed —
    // either the refresh token expired or consent is needed again.
    // This is expected for Work 2 (~12hr token lifetime).
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await instance.acquireTokenPopup({
          scopes: graphScopes.calendar,
          account: account,
        });
        return response.accessToken;
      } catch (popupError) {
        if (popupError.errorCode === 'user_cancelled') {
          throw new Error('Re-authentication cancelled by user.');
        }
        throw popupError;
      }
    }
    throw error;
  }
}

/**
 * Get all currently authenticated accounts from MSAL cache.
 *
 * Returns the list of accounts that have active sessions.
 * In our case, this should be 0, 1, or 2 accounts
 * (Invero and/or Work 2).
 *
 * @returns {Array} List of MSAL account objects
 */
export function getAllAccounts() {
  const instance = getMsalInstance();
  return instance.getAllAccounts();
}

/**
 * Log out a specific account.
 *
 * Removes the account from MSAL's cache. Does NOT trigger
 * a server-side logout (which would log the user out of
 * all Microsoft apps). We just clear the local token.
 *
 * @param {object} account - MSAL account object to remove
 */
export async function logoutAccount(account) {
  const instance = getMsalInstance();

  // Use account-specific logout to only clear this one account,
  // not all accounts. This is important for dual-account scenarios.
  await instance.logoutPopup({
    account: account,
    // mainWindowRedirectUri prevents the main window from navigating
    mainWindowRedirectUri: window.location.origin,
  });
}

/**
 * Check if a token can be silently acquired for an account.
 * Useful for checking if a session is still valid without
 * triggering a popup.
 *
 * @param {object} account - MSAL account object
 * @returns {boolean} True if token was acquired silently
 */
export async function isSessionValid(account) {
  try {
    await acquireToken(account);
    return true;
  } catch {
    return false;
  }
}
